import { getAccessToken } from "../suite/auth";
import { candidateForgeApiBases } from "./generate";

type JobSnap = {
  id: string;
  status?: string;
  error?: string;
  code?: string;
  queuedAhead?: number;
  syncedLyrics?: string;
  matchedWords?: number;
  wordCount?: number;
  modelId?: string;
};

export type AlignProgress = {
  phase: "queued" | "running";
  message: string;
};

export type AlignOk = {
  syncedLyrics: string;
  matchedWords?: number;
  wordCount?: number;
  modelId?: string;
};

function authHeaders(): Record<string, string> {
  const tok = getAccessToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

async function parseJob(res: Response): Promise<JobSnap | null> {
  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("json")) return null;
  try {
    return (await res.json()) as JobSnap;
  } catch {
    return null;
  }
}

async function startJob(
  base: string,
  audio: Blob,
  lyrics: string,
  signal?: AbortSignal,
): Promise<{ job?: JobSnap; error?: string }> {
  const res = await fetch(`${base}/api/musicforge/align/jobs`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": audio.type || "application/octet-stream",
      "X-Musicforge-Lyrics": encodeURIComponent(lyrics),
      "X-Musicforge-Language": "en",
      ...authHeaders(),
    },
    body: audio,
    signal,
  });
  const job = await parseJob(res);
  if (!job?.id) {
    const raw = job ? JSON.stringify(job) : await res.text().catch(() => "");
    return { error: raw.slice(0, 280) || `Lyric sync failed (${res.status})` };
  }
  if (job.status === "error") {
    return { error: job.error || "Lyric sync failed", job };
  }
  return { job };
}

async function pollJob(
  base: string,
  id: string,
  signal: AbortSignal | undefined,
  onProgress?: (p: AlignProgress) => void,
): Promise<JobSnap> {
  const deadline = Date.now() + 6 * 60_000;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const res = await fetch(`${base}/api/musicforge/align/jobs/${encodeURIComponent(id)}`, {
      headers: { Accept: "application/json", ...authHeaders() },
      signal,
    });
    const job = await parseJob(res);
    if (!job) throw new Error(`Lyric sync poll failed (${res.status})`);
    if (job.status === "ok" || job.status === "error") return job;
    const ahead = Number(job.queuedAhead || 0);
    onProgress?.({
      phase: job.status === "queued" ? "queued" : "running",
      message:
        ahead > 0
          ? `Syncing lyrics to the mix (${ahead} ahead)…`
          : "Syncing lyrics to the mix (Whisper)…",
    });
    await new Promise((r) => setTimeout(r, 1_000));
  }
  throw new Error("Lyric sync timed out after 6 minutes.");
}

/** Whisper word times matched to the known lyric sheet → LRC for Karaoke. */
export async function alignTakeLyrics(opts: {
  audio: Blob;
  lyrics: string;
  signal?: AbortSignal;
  onProgress?: (p: AlignProgress) => void;
}): Promise<{ ok: true; result: AlignOk } | { ok: false; error: string }> {
  const lyrics = String(opts.lyrics || "").trim();
  if (!lyrics) return { ok: false, error: "No lyrics to sync." };
  if (!opts.audio?.size) return { ok: false, error: "No audio to sync." };

  const bases = candidateForgeApiBases();
  let lastErr = "Lyric sync failed";
  for (const base of bases) {
    try {
      const started = await startJob(base, opts.audio, lyrics, opts.signal);
      if (started.error) {
        lastErr = started.error;
        continue;
      }
      const job = started.job!;
      const done = await pollJob(base, job.id, opts.signal, opts.onProgress);
      if (done.status === "error") {
        lastErr = done.error || "Lyric sync failed";
        continue;
      }
      const synced = String(done.syncedLyrics || "").trim();
      if (!synced) {
        lastErr = "Lyric sync returned empty LRC";
        continue;
      }
      return {
        ok: true,
        result: {
          syncedLyrics: synced,
          matchedWords: done.matchedWords,
          wordCount: done.wordCount,
          modelId: done.modelId,
        },
      };
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") throw e;
      lastErr = e instanceof Error ? e.message : String(e);
    }
  }
  return { ok: false, error: lastErr };
}
